import { chain, uniq, isEmpty, chunk } from 'lodash';
import { Observable, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import { UserSessionService } from '@app/services/user-session.service';
import { FilterParams } from '@claranet/node-search-filter';
import { Authorization, UserCommunication, UserRole } from '@data/models/authorization.model';
import { Embedded } from '@data/models/embed.model';
import { ListMetadataKeys } from '@data/models/metadata.model';
import { Tag } from '@data/models/tag.model';
import { configuration as userConfiguration, embed as userEmbed, EmbedUser, User, UserHttp } from '@data/models/user.model';
import { USER_SERVICE_API_ROOT, USER_LOGS_API_ROOT } from '@modules/users/user-api-root';
import { Token } from '@data/models/token.model';
import { TagService } from '@shared/components/tag/tag.service';
import { toParams } from '@shared/utils/query-to-params';
import { UserAuthLog } from '../models/user-auth-log.model';

import { AuthorizationService } from './authorization.service';
import { COMPANY_SERVICE_API_ROOT } from './company.service';
import { MetadataService } from './metadata.service';
import { loopWhileNotEmpty } from './utils';

export interface UserCan {
    updateProfile: boolean;
    setIsInternal: boolean;
    setIsBot: boolean;
    saveRoles: boolean;
    saveCommunications: boolean;
    updateNotesTags: boolean;
}

export enum UserMode {
    New,
    Internal,
    InternalMe,
    External,
    ExternalMe,
    Bot
}

@Injectable()
export class UserService implements TagService {

    private errors;
    private _lastFilter: FilterParams;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private userSessionService: UserSessionService,
        private authorizationService: AuthorizationService,
        private metadataService: MetadataService,
    ) {
        this.errors = this.errorService.userServiceErrors;
    }

    public getLastFilter(): FilterParams {
        return this._lastFilter;
    }

    public setLastFilter(lastFilter: FilterParams) {
        this._lastFilter = lastFilter;
    }

    public getUsers(): Observable<User[]> {
        return this.http.get<User[]>(USER_SERVICE_API_ROOT).pipe(
            catchError(this.errorService.errorHandler<User[]>(this.errors, 'Récupération de l\'utilisateur', []))
        );
    }

    public findUsers<T extends userEmbed>(filterParams: FilterParams, embedFields: T[] = []): Observable<EmbedUser<T>[]> {
        const embeds = uniq(embedFields.map((it) => userConfiguration[it].embed));
        const params = toParams({ ...filterParams, embed: !isEmpty(embeds) && embeds.join(',') });
        return this.http.get<{ users: User[]; embedded: Embedded }>(USER_SERVICE_API_ROOT, { params }).pipe(
            map(({ users, embedded }) => User.toUsersEmbedded(users, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedUser<T>[]>(this.errors, 'Récupération des utilisateurs', []))
        );
    }

    public searchUsers(filterParams: FilterParams): Observable<{ users: User[]; totalSize: number }> {
        const params = toParams({ ...filterParams, includeTotal: true });
        return this.http.get<{ users: User[]; totalSize: number }>(USER_SERVICE_API_ROOT, { params }).pipe(
            map(({ users, totalSize }) => ({ users: users.map(user => new User(user)), totalSize })),
            catchError(this.errorService.errorHandler(this.errors, 'Récupération des utilisateurs', { users: [], totalSize: 0 }))
        );
    }

    public fetchUnpaginatedUsers<T extends userEmbed>(filterParams: Omit<FilterParams, 'limit' | 'page'>, embedFields: T[] = []): Observable<EmbedUser<T>[]> {
        return loopWhileNotEmpty((page: number) => {
            return this.findUsers({ ...filterParams, page }, embedFields);
        });
    }

    public async fetchUsersByChunkIds<T extends userEmbed>(userIds: string[], embedFields: T[] = []): Promise<EmbedUser<T>[]> {
        const userIdsChunk = chunk(userIds, 100);
        const userPromise = userIdsChunk.map((ids) =>
            lastValueFrom(this.findUsers({ filter: `id:(${ids.join('|')})` }, embedFields))
        );
        return (await Promise.all(userPromise)).flat();
    }

    /**
     * Find company users
     * @param {string} companyId
     * @returns {Observable<User[]>}
     */
    public findUsersByCompany(companyId: string, filters?: FilterParams): Observable<User[]> {
        return this.http.get<{ users: User[] }>(`${COMPANY_SERVICE_API_ROOT}/${companyId}/users`, {
            params: toParams(filters)
        }).pipe(
            map((result) => result.users),
            catchError(this.errorService.errorHandler<User[]>(this.errors, 'Récupération des utilisateurs de la companie', []))
        );
    }

    public deleteUser(userId: string): Observable<User> {
        return this.http.delete<User>(USER_SERVICE_API_ROOT + '/' + userId).pipe(
            catchError(this.errorService.errorHandler<User>(this.errors, 'Suppression d\'un utilisateur', null))
        );
    }

    public getUser<T extends userEmbed>(
        userId: string,
        { embedFields = [], includeRemoved = false }: { embedFields?: T[]; includeRemoved?: boolean } = {}
    ): Observable<EmbedUser<T>> {
        const embeds = uniq(embedFields.map((it) => userConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });
        return this.http.get<User & { embedded: Embedded }>(
            USER_SERVICE_API_ROOT + '/' + userId,
            { params: params.set('showDeleted', `${includeRemoved}`) }
        ).pipe(
            map(({ embedded, ...user }) => User.toUserEmbedded(user, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedUser<T>>(this.errors, 'Récupération d\'un utilisateur', null))
        );
    }

    public findRoles(userId: string): Observable<UserRole[]> {
        return this.http.get<{ userRoles: UserRole[] }>(USER_SERVICE_API_ROOT + '/' + userId + '/roles').pipe(
            map((result) => result.userRoles),
            catchError(this.errorService.errorHandler<UserRole[]>(this.errors, 'Récupération des droits d\'un utilisateur', []))
        );
    }

    public findCommunications(userId: string): Observable<UserCommunication[]> {
        return this.http.get<{ communications: UserCommunication[] }>(USER_SERVICE_API_ROOT + '/' + userId + '/communications').pipe(
            map((result) => result.communications),
            catchError(this.errorService.errorHandler<UserCommunication[]>(
                this.errors,
                'Récupération des communications d\'un utilisateur',
                []
            ))
        );
    }

    public fetchTags(filterParams: FilterParams): Observable<Tag[]> {
        const params = toParams(filterParams);
        return this.http.get<{ tags: Tag[] }>(USER_SERVICE_API_ROOT + '/tags', { params }).pipe(
            map((result) => result.tags),
            catchError(this.errorService.errorHandler<Tag[]>(this.errors, 'Récupération des tags', []))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(USER_SERVICE_API_ROOT, filterParams, this.errors);
    }

    /**
     * Vreate user
     * @param {User} user
     * @returns {Observable<User>}
     */
    public createUser(user: User): Observable<User> {
        return this.http.post<User>(USER_SERVICE_API_ROOT, new UserHttp(user)).pipe(
            map((result) => new User(result)),
            catchError(this.errorService.errorHandler<User>(this.errors, 'Création d\'un utilisateur', null))
        );
    }

    public updateUser(user: User): Observable<User> {
        return this.http.patch<User>(USER_SERVICE_API_ROOT + '/' + user.id, new UserHttp(user)).pipe(
            map((result) => new User(result)),
            catchError((error) => {
                if (error.status === 403 && user.isBot) {
                    return this.errorService.errorHandler<User>(this.errors, 'Il faut être le créateur d\'un bot pour le modifier.', null)(error);
                }
                return this.errorService.errorHandler<User>(this.errors, 'Modification d\'un utilisateur', null)(error);
            })
        );
    }

    public updateUserMetadata(userId: string, metadata: Map<string, string>): Observable<User> {
        const parsedMetadata = {};
        metadata.forEach((value: string, key: string) => {
            parsedMetadata[key] = value.trim();
        });

        return this.http.post<User>(USER_SERVICE_API_ROOT + `/${userId}:setMetadata`, parsedMetadata)
            .pipe(
                map(_user => new User(_user)),
                catchError(this.errorService.errorHandler<User>(this.errors, 'Modification des metadatas d\'un utilisateur', null))
            );
    }

    public async saveUser(user: User, userAuthorizations?: Authorization[]): Promise<User> {
        const userResult = await lastValueFrom((user.id ?
            this.updateUser(user) :
            this.createUser(user)
        ));

        if (userAuthorizations && userResult) {
            const userCan = this.defineUserCan(userResult);

            if (userCan.saveRoles && user.id !== this.userSessionService.getUserSession().id) {
                await this.saveRoles(userResult.id, userAuthorizations);
            }
            if (userCan.saveCommunications) {
                await this.saveCommunications(userResult.id, userAuthorizations);
            }
        }
        return userResult;
    }

    public getTokensByUser(userId: string): Observable<Token[]> {
        return this.http.get<{ tokens: Token[] }>(`${USER_SERVICE_API_ROOT}/${userId}/tokens`).pipe(
            map(result => result.tokens),
            catchError(this.errorService.errorHandler<Token[]>(this.errors, 'Récupération des tokens d\'un utilisateur', []))
        );
    }

    public createToken(userId: string, token: Omit<Token, 'audience'>): Observable<Token> {
        return this.http.post<Token>(`${USER_SERVICE_API_ROOT}/${userId}/tokens`, token).pipe(
            catchError(this.errorService.errorHandler<Token>(this.errors, 'Création d\'un token', null))
        );
    }

    public updateToken(userId: string, tokenIdentifier: string, token: Omit<Token, 'audience'>): Observable<Token> {
        return this.http.patch<Token>(`${USER_SERVICE_API_ROOT}/${userId}/tokens/${tokenIdentifier}`, token).pipe(
            catchError(this.errorService.errorHandler<Token>(this.errors, 'Modification d\'un token', null))
        );
    }

    public deleteToken(userId: string, tokenIdentifier: string): Observable<Token> {
        return this.http.delete<Token>(`${USER_SERVICE_API_ROOT}/${userId}/tokens/${tokenIdentifier}`).pipe(
            catchError(this.errorService.errorHandler<Token>(this.errors, 'Suppression d\'un token', null))
        );
    }

    // define user mode: you cannot edit an internal user but your own comm
    public defineUserMode(user: User): UserMode {
        const currentUser = this.userSessionService.getUserSession();

        if (!user) {
            return UserMode.New;
        }

        if (user.isBot) {
            // Can update all info but not make it an internal individual
            return UserMode.Bot;
        }

        if (user.isInternal) {
            // internal user infos are updated from AD
            // you can only change YOUR profile
            return user.id === currentUser.id ? UserMode.InternalMe : UserMode.Internal;
        }

        // external users are created within synapps
        // you can update all info but change isInternal
        return user.id === currentUser.id ? UserMode.ExternalMe : UserMode.External;
    }

    public defineUserCan(user: User): UserCan {
        const userMode = this.defineUserMode(user);

        const userCan = {
            updateProfile: false,
            setIsInternal: false,
            setIsBot: false,
            saveRoles: false,
            saveCommunications: false,
            updateNotesTags: false
        };

        // handle user modes
        switch (userMode) {
            case UserMode.New:
                userCan.updateProfile = true;
                userCan.setIsInternal = true;
                userCan.setIsBot = true;
                userCan.saveRoles = true;
                userCan.saveCommunications = true;
                userCan.updateNotesTags = true;
                break;
            case UserMode.Bot:
            case UserMode.External:
            case UserMode.ExternalMe:
                userCan.updateProfile = true;
                userCan.saveRoles = true;
                userCan.saveCommunications = true;
                userCan.updateNotesTags = true;
                break;

            case UserMode.InternalMe:
                userCan.updateNotesTags = true;
                userCan.saveRoles = true;
                userCan.saveCommunications = true;
                break;

            case UserMode.Internal:
            default:
                break;
        }

        return userCan;
    }

    public exportUsers(filterParams: FilterParams): Observable<{ content: string }> {
        const params = toParams(filterParams);
        return this.http.get<{ content: string }>(`${USER_SERVICE_API_ROOT}:export`, { params }).pipe(
            catchError(this.errorService.errorHandler(this.errors, 'Export d\'utilisateurs', null)),
        );
    }

    public getUserAuthLogs(userId: string): Observable<UserAuthLog[]> {
        return this.http.get<{ logs: any[] }>(`${USER_LOGS_API_ROOT}/${userId}/logs-event`).pipe(
            map(({ logs }) => logs.map(UserAuthLog.toUserAuthLog)),
            catchError(this.errorService.errorHandler<UserAuthLog[]>(this.errors, 'Récupération des logs d\'un utilisateur', null))
        );
    }

    private async saveRoles(userId: string, userAuthorizations: Authorization[]): Promise<UserRole[]> {
        const roles = this.convertUserRolesToPayload(userAuthorizations);
        return await lastValueFrom(this.authorizationService.updateRolesByUser(userId, roles));
    }

    private async saveCommunications(userId: string, userAuthorizations: Authorization[]): Promise<UserCommunication[]> {
        const communications = this.convertUserCommunicationsToPayload(userAuthorizations);
        return await lastValueFrom(this.authorizationService.updateCommunicationsByUser(userId, communications));
    }

    private convertUserCommunicationsToPayload(userAuthorizations: Authorization[]): UserCommunication[] {
        return chain(userAuthorizations)
            .filter(userAuthorization => userAuthorization.roles.length > 0)
            .map(userAuthorization => {
                return {
                    projectId: userAuthorization.projectId,
                    communicationTypes: userAuthorization.communicationTypes
                };
            })
            .value();
    }

    private convertUserRolesToPayload(userAuthorizations: Authorization[]): UserRole[] {
        return chain(userAuthorizations)
            .map(userAuthorization => {
                return {
                    projectId: userAuthorization.projectId,
                    roles: userAuthorization.roles
                };
            })
            .value();
    }

}
